import { DynamicForm } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { fields } from "../../constants";
import { useContactForm } from "../../Pages.hook";

const FormDetails = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { openToast } = useNotification();
  const navigate = useNavigate();
  const query = useQuery();

  const {
    data: pagesDataById,
    isLoading: isLoadingPagesById,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const { data: contactFormData, isLoading: isLoadingContactUsForm, refetch: refetchContactUsForm } = useContactForm();

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }

    refetchContactUsForm();
  }, [query.get("editId"), query.get("id")]);

  const contactDataFields = {
    initialValues: {
      // Contact form fields (mapped to API response)
      field_name_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_name_en)
        : decodeHTML(contactFormData?.[0]?.field_name_en),

      field_name_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_name_ms)
        : decodeHTML(contactFormData?.[0]?.field_name_ms),

      field_name_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_name_zh)
        : decodeHTML(contactFormData?.[0]?.field_name_zh),

      field_mobile_number_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_mobile_number_en)
        : decodeHTML(contactFormData?.[0]?.field_mobile_number_en),

      field_mobile_number_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_mobile_number_ms)
        : decodeHTML(contactFormData?.[0]?.field_mobile_number_ms),

      field_mobile_number_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_mobile_number_zh)
        : decodeHTML(contactFormData?.[0]?.field_mobile_number_zh),

      field_email_address_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_email_address_en)
        : decodeHTML(contactFormData?.[0]?.field_email_address_en),

      field_email_address_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_email_address_ms)
        : decodeHTML(contactFormData?.[0]?.field_email_address_ms),

      field_email_address_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_email_address_zh)
        : decodeHTML(contactFormData?.[0]?.field_email_address_zh),

      field_category_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_category_en)
        : decodeHTML(contactFormData?.[0]?.field_category_en),

      field_category_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_category_ms)
        : decodeHTML(contactFormData?.[0]?.field_category_ms),

      field_category_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_category_zh)
        : decodeHTML(contactFormData?.[0]?.field_category_zh),

      field_message_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_message_en)
        : decodeHTML(contactFormData?.[0]?.field_message_en),

      field_message_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_message_ms)
        : decodeHTML(contactFormData?.[0]?.field_message_ms),

      field_message_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_message_zh)
        : decodeHTML(contactFormData?.[0]?.field_message_zh),

      field_note_text_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_note_text_en)
        : decodeHTML(contactFormData?.[0]?.field_note_text_en),

      field_note_text_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_note_text_ms)
        : decodeHTML(contactFormData?.[0]?.field_note_text_ms),

      field_note_text_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_note_text_zh)
        : decodeHTML(contactFormData?.[0]?.field_note_text_zh),

      field_submit_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_submit_en)
        : decodeHTML(contactFormData?.[0]?.field_submit_en),

      field_submit_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_submit_ms)
        : decodeHTML(contactFormData?.[0]?.field_submit_ms),

      field_submit_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_submit_zh)
        : decodeHTML(contactFormData?.[0]?.field_submit_zh),
    },
    fields: fields.formDetails,
  };

  const onSubmit = async (
    formdata: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    const payload = {
      action: action,
      method: method,
      module: "Pages",
      subModule: "aboutEraman_contactUs_formDetails",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && {
          id: pagesDataById?.payload?.id || contactFormData?.[0]?.id,
        }),
        ...formdata,
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status === "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status === "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status === "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status === "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <DynamicForm
      isLoading={isLoadingContactUsForm || isLoadingPagesById}
      config={contactDataFields}
      onSubmit={(data, buttonValue, reviewNotes) =>
        onSubmit(
          data,
          `${eramanHost}/api/contact-us-page-data-update-api/${pagesDataById?.payload?.id || contactFormData?.[0]?.id}`,
          "UPDATE",
          "PATCH",
          buttonValue === "save" ? "DRAFT" : "PENDING",
          reviewNotes
        )
      }
    />
  );
};

export default FormDetails;
